// MST
import { types, flow, cast } from 'mobx-state-tree'

import {
  IGalleryGetAllAttrs,
  IGalleryGetAttrs,
  IGallerySaveAttrs,
  IGalleryDeleteImageAttrs,
  IGalleryGetGalleryImagesAttrs,
  IGalleryUpdateImageAttrs,
} from 'interfaces/back-office/content-management/gallery/gallery/gallery-view.interface'
import {
  IGalleryApiDeleteImageResponse,
  IGalleryApiGetAllResponse,
  IGalleryApiGetGalleryImagesPaginatedResponse,
  IGalleryApiGetGalleryImagesResponse,
  IGalleryApiGetResponse,
  IGalleryApiPaginatedResponse,
  IGalleryApiSaveResponse,
  IGalleryApiUpdateImageResponse,
} from 'interfaces/back-office/content-management/gallery/gallery/gallery-api.interface'
import { IGalleryDetail, IGalleryImage } from 'interfaces/back-office/content-management/gallery/gallery'

// Extensions
import { withEnvironment } from 'models/extensions/with-environment'
import { withHelper_baseResponsePaginationTypes } from 'models/extensions/with-helper'

// Lodash
import omit from 'lodash/omit'
import cloneDeep from 'lodash/cloneDeep'

// Utils
import { dateUtils_formatDate } from 'utils/common/date.utils'
import { commonUtils_preventDuplicate, commonUtils_renderTableNumber } from 'utils/common/common.utils'

// Gallery image type
type TGalleryImage = {
  id: number | null
  itemsId: number | null
  imageURL: string | null
}

// Gallery Image Model
const GalleryImage = types.model('GalleryImage').props({
  id: types.maybeNull(types.number),
  itemsId: types.maybeNull(types.number),
  imageURL: types.maybeNull(types.string),
})

// Gallery Paginated Model
const GalleryImagePaginated = types.model('GalleryImagePaginated').props(withHelper_baseResponsePaginationTypes(GalleryImage))

// Gallery Props
const galleryProps = {
  number: types.maybeNull(types.number),
  id: types.maybeNull(types.number),
  name: types.maybeNull(types.string),
  images: types.maybeNull(types.array(GalleryImage)),
  createdAt: types.maybeNull(types.string),
  updatedAt: types.maybeNull(types.string),
}

// Gallery List Model
const Galleries = types
  .model('Galleries')
  .props(withHelper_baseResponsePaginationTypes(types.model('GalleryList').props(omit(galleryProps))))

// Gallery Detail Model
const GalleryDetail = types.model('GalleryDetail').props(omit(galleryProps, ['number']))

const GalleryStoreModel = types
  .model('Gallery')
  .props({
    gallery_isLoading: types.optional(types.boolean, false),
    gallery_isSaveLoading: types.optional(types.boolean, false),
    gallery_isImageLoading: types.optional(types.boolean, false),
    gallery_galleries: types.optional(Galleries, {}),
    gallery_detail: types.optional(GalleryDetail, {}),
    gallery_images: types.optional(GalleryImagePaginated, {}),
  })
  .actions(self => ({
    gallery_SET_LOADING: (payload: boolean): void => {
      self.gallery_isLoading = payload
    },
    gallery_SET_SAVE_LOADING: (payload: boolean): void => {
      self.gallery_isSaveLoading = payload
    },
    gallery_SET_IMAGE_LOADING: (payload: boolean): void => {
      self.gallery_isImageLoading = payload
    },
    gallery_SET_GALLERIES: (payload: IGalleryApiPaginatedResponse): void => {
      const mapPayload = {
        ...payload,
        list: payload.list.map((list, index) => {
          return {
            ...list,
            number: commonUtils_renderTableNumber(index, payload),
            createdAt: dateUtils_formatDate(list.createdAt!, { tableFormat: true }),
            updatedAt: dateUtils_formatDate(list.updatedAt!, { tableFormat: true }),
          }
        }),
      }

      self.gallery_galleries = cast(mapPayload)
    },
    gallery_SET_GALLERY: (payload: IGalleryDetail): void => {
      self.gallery_detail = cast(payload)
    },
    gallery_SET_GALLERY_IMAGES: (payload: IGalleryApiGetGalleryImagesPaginatedResponse): void => {
      self.gallery_images = cast(
        cloneDeep({
          ...payload,
          list: commonUtils_preventDuplicate([...self.gallery_images.list, ...payload.list], 'id'),
        }),
      )
    },
    gallery_CLEAR_GALLERIES: (): void => {
      self.gallery_galleries = cast({})
    },
    gallery_CLEAR_GALLERY: (): void => {
      self.gallery_detail = cast({})
    },
    gallery_CLEAR_GALLERY_IMAGES: (): void => {
      self.gallery_images = cast({})
    },
    gallery_SET_NEW_IMAGE: (payload: IGalleryImage): void => {
      self.gallery_images = cast({
        ...self.gallery_images,
        list: [...self.gallery_images.list, payload],
      })
    },
    gallery_UPDATE_GALLERY_IMAGE: (payload: IGalleryImage): void => {
      self.gallery_images = cast({
        ...self.gallery_images,
        list: self.gallery_images.list.map((list: TGalleryImage) => (list.id === payload.id ? payload : list)),
      })
    },
    gallery_DELETE_GALLERY_IMAGE: (payload: number): void => {
      self.gallery_images = cast({
        ...self.gallery_images,
        list: self.gallery_images.list.filter((list: TGalleryImage) => list.id !== payload),
      })
    },
  }))
  .extend(withEnvironment)
  .actions(self => ({
    /**
     * @description Fetch galleries
     *
     * @param {IGalleryGetAllAttrs} payload
     *
     * @return {any} any
     */
    gallery_fetchGalleries: flow(function* (payload: IGalleryGetAllAttrs) {
      self.gallery_SET_LOADING(true)

      const response: IGalleryApiGetAllResponse = yield self.environment.galleryApi.gallery_getGalleries({
        body: { ...payload.body },
        requestConfig: { ...payload.requestConfig },
      })
      if (response.ok) self.gallery_SET_GALLERIES(response.res.data)

      self.gallery_SET_LOADING(false)
    }),

    /**
     * @description Fetch gallery
     *
     * @param {IGalleryGetAttrs} payload
     *
     * @return {any} any
     */
    gallery_fetchGallery: flow(function* (payload: IGalleryGetAttrs) {
      self.gallery_SET_LOADING(true)

      const response: IGalleryApiGetResponse = yield self.environment.galleryApi.gallery_getGallery({
        params: { id: payload.params.id },
        requestConfig: { ...payload.requestConfig },
      })
      if (response.ok) {
        self.gallery_SET_GALLERY(response.res.data)

        // Do when response success
        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        // Do when response error
        if (payload?.onError) payload.onError()
      }

      self.gallery_SET_LOADING(false)
    }),

    /**
     * @description Save gallery
     *
     * @param {IGallerySaveAttrs} payload
     *
     * @return {any} any
     */
    gallery_saveGallery: flow(function* (payload: IGallerySaveAttrs) {
      self.gallery_SET_SAVE_LOADING(true)

      const response: IGalleryApiSaveResponse = yield self.environment.galleryApi.gallery_saveGallery({
        params: { ...payload.params },
        body: { ...payload.body },
        requestConfig: { ...payload.requestConfig },
      })
      if (response.ok) {
        // Do when response success
        if (payload?.onSuccess) payload.onSuccess(response.res.data)
      }

      self.gallery_SET_SAVE_LOADING(false)
    }),

    /**
     * @description Update image
     *
     * @param {IGalleryUpdateImageAttrs} payload
     *
     * @return {any} any
     */
    gallery_updateImage: flow(function* (payload: IGalleryUpdateImageAttrs) {
      self.gallery_SET_SAVE_LOADING(true)

      const response: IGalleryApiUpdateImageResponse = yield self.environment.galleryApi.gallery_updateImage({
        params: { ...payload.params },
        body: { ...payload.body },
        requestConfig: { ...payload.requestConfig, headers: { 'Content-Type': 'multipart/form-data' } },
      })

      if (response.ok) {
        self.gallery_UPDATE_GALLERY_IMAGE(response.res.data)

        // Do when response success
        if (payload?.onSuccess) payload.onSuccess()
      }

      self.gallery_SET_SAVE_LOADING(false)
    }),

    /**
     * @description Delete image
     *
     * @param {IGalleryDeleteAttrs} payload
     *
     * @return {any} any
     */
    gallery_deleteImage: flow(function* (payload: IGalleryDeleteImageAttrs) {
      self.gallery_SET_SAVE_LOADING(true)

      const response: IGalleryApiDeleteImageResponse = yield self.environment.galleryApi.gallery_deleteImage({
        params: { ...payload.params },
        requestConfig: { ...payload.requestConfig },
      })
      if (response.ok) {
        // Do when response success
        if (payload?.onSuccess) payload.onSuccess()
      }

      self.gallery_SET_SAVE_LOADING(false)
    }),

    /**
     * @description Get gallery images
     *
     * @param {IGalleryGetGalleryImagesAttrs} payload
     *
     * @return {any} any
     */
    gallery_fetchGalleryImages: flow(function* (payload: IGalleryGetGalleryImagesAttrs) {
      self.gallery_SET_IMAGE_LOADING(true)

      const response: IGalleryApiGetGalleryImagesResponse = yield self.environment.galleryApi.gallery_getGalleryImages({
        params: { ...payload.params },
        body: { ...payload.body },
        requestConfig: { ...payload.requestConfig },
      })
      if (response.ok) {
        self.gallery_SET_GALLERY_IMAGES(response.res.data)

        // Do when response success
        if (payload?.onSuccess) payload.onSuccess()
      }

      self.gallery_SET_IMAGE_LOADING(false)
    }),
  }))

export const createGalleryStoreDefaultModel = () => types.optional(GalleryStoreModel, {})
